/**
 * The shareable card: a rendered garden inside a branded frame.
 *
 * Pure 2D canvas over an image somebody else captured. It knows nothing about
 * Three.js, the garden or storage — hand it a picture and four lines of text
 * and it hands back a canvas.
 *
 * ## Why the frame exists at all
 *
 * A bare screenshot of the garden is a nice picture of nothing in particular.
 * The frame is what makes a shared image say where it came from rather than
 * "some plants" — which is most of the point of letting people share it.
 *
 * Portrait 1080×1350 because that is what messaging apps and social feeds show
 * without cropping, and a cropped frame is worse than no frame.
 */

import { BRAND } from '../content/brand';

const W = 1080;
const H = 1350;

/** Image well: 4:3, centred, with the title above and the caption below. */
const WELL = { x: 60, y: 268, w: 960, h: 720, r: 28 };

const { ink: INK, inkDeep: INK_DEEP, accent: GOLD, accentSoft: GOLD_SOFT, paper: PAPER } =
  BRAND.plate;
const MUTED = rgba(PAPER, 0.62);

/** `#d9a441`, 0.4 → `rgba(217, 164, 65, 0.4)`. The plate's alphas follow its palette. */
function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

const DISPLAY = `'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, 'Times New Roman', serif`;
const UI = `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;

export interface CardText {
  /** Empty when the gardener chose not to put their name on it. */
  name: string;
  /** "18 trees grown", already pluralised. */
  headline: string;
  /** "Sunken Eco-Wadi · 36 beds · 24 Aug 2026". */
  meta: string;
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Draw text with tracking.
 *
 * `ctx.letterSpacing` is not universal, so this measures and places each glyph
 * when it is missing. Only used on the two short Latin kickers — never on the
 * Arabic, where splitting a string into characters would break the joining
 * and render the name as disconnected letterforms.
 */
function tracked(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  y: number,
  spacing: number,
): void {
  const styled = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in styled) {
    styled.letterSpacing = `${spacing}px`;
    ctx.textAlign = 'center';
    ctx.fillText(text, cx, y);
    styled.letterSpacing = '0px';
    return;
  }

  const chars = [...text];
  const width =
    chars.reduce((sum, ch) => sum + ctx.measureText(ch).width, 0) +
    spacing * (chars.length - 1);
  let x = cx - width / 2;
  ctx.textAlign = 'left';
  for (const ch of chars) {
    ctx.fillText(ch, x, y);
    x += ctx.measureText(ch).width + spacing;
  }
  ctx.textAlign = 'center';
}

/**
 * Draw a source to fill a rect, cropping the overflow. Never letterboxed: a
 * band of background inside the frame reads as a broken image.
 *
 * This is also what CSS `object-fit: cover` does, which is why compositing the
 * camera feed with it reproduces exactly what the player was looking at.
 */
export function drawCover(
  ctx: CanvasRenderingContext2D,
  src: CanvasImageSource,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const sw = sourceWidth(src) || w;
  const sh = sourceHeight(src) || h;
  const scale = Math.max(w / sw, h / sh);
  ctx.drawImage(src, x + (w - sw * scale) / 2, y + (h - sh * scale) / 2, sw * scale, sh * scale);
}

function sourceWidth(src: CanvasImageSource): number {
  const v = src as HTMLVideoElement;
  return v.videoWidth || (src as HTMLCanvasElement).width || 0;
}

function sourceHeight(src: CanvasImageSource): number {
  const v = src as HTMLVideoElement;
  return v.videoHeight || (src as HTMLCanvasElement).height || 0;
}

/** Shrink until it fits, rather than overflowing the frame. */
function fitted(
  ctx: CanvasRenderingContext2D,
  text: string,
  max: number,
  start: number,
  font: (size: number) => string,
): number {
  let size = start;
  while (size > 18) {
    ctx.font = font(size);
    if (ctx.measureText(text).width <= max) break;
    size -= 2;
  }
  return size;
}

/**
 * Compose the card. `shot` is the garden as rendered, cropped to fill the
 * well — never letterboxed, because a band of background inside the frame
 * reads as a broken image.
 */
export function drawCard(shot: CanvasImageSource, text: CardText): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const backdrop = ctx.createLinearGradient(0, 0, 0, H);
  backdrop.addColorStop(0, INK);
  backdrop.addColorStop(1, INK_DEEP);
  ctx.fillStyle = backdrop;
  ctx.fillRect(0, 0, W, H);

  // A hairline gold rule inset from the edge — the whole reason the card reads
  // as a plate rather than a screenshot with text on it.
  ctx.strokeStyle = rgba(GOLD, 0.34);
  ctx.lineWidth = 2;
  roundedRect(ctx, 26, 26, W - 52, H - 52, 22);
  ctx.stroke();

  /* ---- Masthead --------------------------------------------------- */

  ctx.fillStyle = GOLD;
  ctx.font = `600 26px ${UI}`;
  ctx.textBaseline = 'alphabetic';
  tracked(ctx, BRAND.share.masthead, W / 2, 122, 7);

  ctx.fillStyle = GOLD_SOFT;
  ctx.font = `44px ${DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.fillText(BRAND.nameAr, W / 2, 186);

  ctx.strokeStyle = rgba(GOLD, 0.4);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 90, 216);
  ctx.lineTo(W / 2 + 90, 216);
  ctx.stroke();

  /* ---- The garden -------------------------------------------------- */

  ctx.save();
  roundedRect(ctx, WELL.x, WELL.y, WELL.w, WELL.h, WELL.r);
  ctx.clip();

  /*
   * Ground the well before the shot goes in.
   *
   * The WebGL canvas is drawn with `clearAlpha(0)` so a camera feed can show
   * through it, so a capture with nothing composited behind it is a
   * transparent PNG of the garden alone. `camera` mode hands the feed in and
   * the shot arrives opaque; `webxr` cannot, because the runtime composites
   * the passthrough and the page never sees it. This gradient is what the
   * headset case falls back to — a plausible sky and floor rather than a hole.
   */
  const ground = ctx.createLinearGradient(0, WELL.y, 0, WELL.y + WELL.h);
  ground.addColorStop(0, '#8f8296');
  ground.addColorStop(0.42, '#e2cfae');
  ground.addColorStop(1, '#cbb894');
  ctx.fillStyle = ground;
  ctx.fillRect(WELL.x, WELL.y, WELL.w, WELL.h);

  drawCover(ctx, shot, WELL.x, WELL.y, WELL.w, WELL.h);
  ctx.restore();

  roundedRect(ctx, WELL.x, WELL.y, WELL.w, WELL.h, WELL.r);
  ctx.strokeStyle = rgba(GOLD, 0.28);
  ctx.lineWidth = 2;
  ctx.stroke();

  /* ---- Caption ----------------------------------------------------- */

  let y = WELL.y + WELL.h + 96;

  if (text.name) {
    const size = fitted(ctx, text.name, W - 200, 62, (s) => `${s}px ${DISPLAY}`);
    ctx.fillStyle = PAPER;
    ctx.font = `${size}px ${DISPLAY}`;
    ctx.textAlign = 'center';
    ctx.fillText(text.name, W / 2, y);
    y += 62;
  }

  const headSize = fitted(ctx, text.headline, W - 180, 40, (s) => `${s}px ${DISPLAY}`);
  ctx.fillStyle = GOLD_SOFT;
  ctx.font = `${headSize}px ${DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.fillText(text.headline, W / 2, y);
  y += 52;

  const metaSize = fitted(ctx, text.meta, W - 160, 24, (s) => `${s}px ${UI}`);
  ctx.fillStyle = MUTED;
  ctx.font = `${metaSize}px ${UI}`;
  ctx.fillText(text.meta, W / 2, y);

  ctx.fillStyle = rgba(PAPER, 0.34);
  ctx.font = `500 20px ${UI}`;
  tracked(ctx, BRAND.share.footer, W / 2, H - 62, 4);

  return canvas;
}
